import { IStepOrderStatus, TInterfaceErrorCode } from '@/interfaces/order';
import { EOrderStatus, EOrderType } from '../enums/EOrder';
import { IColor } from '../interfaces/color';
import { EShippingType } from '@/enums/EShippingType';
import { EPaymentReceivedStatus, EPaymentType } from '@/enums/ECollector';

const getOrderStatusName = (
  status: EOrderStatus,
  shippingType?: EShippingType,
  all?: boolean,
  interfaceErrorCode?: TInterfaceErrorCode,
): string => {
  switch (status) {
    case EOrderStatus.APPROVED:
      return 'Send';
    case EOrderStatus.COMPLETED:
      if (all) return 'Completed/Dispatched';
      if (shippingType !== EShippingType.PUIS) return 'Dispatched';
      return 'Completed';
    case EOrderStatus.CANCEL:
      return 'Canceled';
    case EOrderStatus.PENDING:
      return 'Draft';
    case EOrderStatus.REJECTED:
      if (interfaceErrorCode == 'Sufficient_Inventory') return 'Sufficient INV';
      return 'Failed';
    case EOrderStatus.RECEIVED:
      return 'Received';
    // case EOrderStatus.CONFIRM:
    //   return 'Confirm Doc';
    case EOrderStatus.POD:
      return 'POD';
    case EOrderStatus.RETURN:
      return 'Retrun';
    case EOrderStatus.PARTIAL_RETURN:
      return 'Partial return';
    default:
      return '';
  }
};

const getOrderStatusColor = (
  status: EOrderStatus,
  shippingType?: EShippingType,
): IColor => {
  let color: IColor = {
    bg: 'bg-blue-200',
    text: 'text-black',
  };
  switch (status) {
    case EOrderStatus.COMPLETED:
      if (shippingType !== EShippingType.PUIS) {
        color = {
          bg: 'bg-purple-100',
          text: 'text-purple-700',
        };
      } else {
        color = {
          bg: 'bg-green-100',
          text: 'text-green-700',
        };
      }

      break;
    case EOrderStatus.PENDING:
      color = {
        bg: 'bg-yellow-100',
        text: 'text-yellow-700',
      };
      break;
    case EOrderStatus.CONFIRM:
      color = {
        bg: 'bg-pink-100',
        text: 'text-pink-700',
      };
      break;
    case EOrderStatus.CANCEL:
      color = {
        bg: 'bg-red-100',
        text: 'text-red-500',
      };
      break;
    case EOrderStatus.RETURN:
    case EOrderStatus.PARTIAL_RETURN:
      color = {
        bg: 'bg-cyan-100',
        text: 'text-cyan-500',
      };
      break;
    case EOrderStatus.REJECTED:
      color = {
        bg: 'bg-red-100',
        text: 'text-red-500',
      };
      break;
    case EOrderStatus.RECEIVED:
      color = {
        bg: 'bg-orange-100',
        text: 'text-orange-500',
      };
      break;
    case EOrderStatus.POD:
      color = {
        bg: 'bg-green-100',
        text: 'text-green-700',
      };
      break;
  }
  return color;
};

const getOrderStatusOptions = () =>
  Object.entries(EOrderStatus)
    .filter(
      ([key, value]) => isNaN(Number(key)) && value !== EOrderStatus.CONFIRM,
    )
    .map(([, value]) => ({
      label: getOrderStatusName(value as EOrderStatus, undefined, true),
      value: value as EOrderStatus,
    }));

const getOrderStepStatus = (status: EOrderStatus) => {
  let step: IStepOrderStatus = {
    status: undefined,
    step: 1,
  };
  switch (status) {
    case EOrderStatus.RECEIVED:
      step = {
        step: 2,
        status: 'process',
      };
      break;
    case EOrderStatus.COMPLETED:
      step = {
        step: 3,
        status: 'finish',
      };
      break;
    case EOrderStatus.PENDING:
      step = {
        step: 1,
        status: 'process',
      };
      break;
    case EOrderStatus.CANCEL:
      step = {
        step: 3,
        status: 'error',
      };
      break;
  }
  return step;
};

const isEditOrderInfo = (status: EOrderStatus, isConfirm: boolean) => {
  // return (
  //   [
  //     EOrderStatus.PENDING,
  //     EOrderStatus.APPROVED,
  //     EOrderStatus.RECEIVED,
  //     EOrderStatus.REJECTED,
  //   ].includes(status) && !isConfirm
  // );
  return !isConfirm;
};

const isSuperUserEditOrderInfo = (
  status: EOrderStatus,
  isConfirm: boolean,
  transport?: EShippingType,
  isComplete?: boolean,
) => {
  if (transport === EShippingType.PUIS) {
    return isConfirm && !isComplete;
  }
  return isConfirm;
};

const getPodUrl = (link: string) => {
  return `${process.env.REACT_APP_PUBLIC_BASE_PIONEER_URL}/view_pdf/${link}`;
};

const getPaymentTypeName = (status: EPaymentType) => {
  switch (status) {
    case EPaymentType.CHEQUE:
      return 'CHEQUE';
    case EPaymentType.BANK_TRANSFER:
      return 'โอนเงิน';
  }
};

const getOrderTypeName = (status: EOrderType) => {
  switch (status) {
    case EOrderType.EXPORT:
      return 'Export';
    case EOrderType.INTERNAL:
      return 'ในประเทศ';
  }
};

const getPaymentTypeOptions = () =>
  Object.entries(EPaymentType)
    .filter(([key]) => isNaN(Number(key)))
    .map(([, value]) => ({
      label: getPaymentTypeName(value as EPaymentType),
      value: value as EPaymentType,
    }));

const getPaymentTypeColor = (status: EPaymentType): IColor => {
  let color: IColor = {
    bg: 'bg-blue-200',
    text: 'text-black',
  };
  switch (status) {
    case EPaymentType.CHEQUE:
      color = {
        bg: 'bg-green-100',
        text: 'text-green-700',
      };
  }
  return color;
};

const getOrderTypeColor = (status: EOrderType): IColor => {
  let color: IColor = {
    bg: 'bg-blue-200',
    text: 'text-black',
  };
  switch (status) {
    case EOrderType.EXPORT:
      color = {
        bg: 'bg-green-100',
        text: 'text-green-700',
      };
  }
  return color;
};

const getPaymentReceivedStatusName = (status: EPaymentReceivedStatus) => {
  switch (status) {
    case EPaymentReceivedStatus.POD:
      return 'รอวางบิล';
    case EPaymentReceivedStatus.BILLED:
      return 'รอโอนเงิน/รอฝากเช็ค';
    case EPaymentReceivedStatus.RECEIVED:
      return 'ตรวจสอบยอดเงิน';
    case EPaymentReceivedStatus.DONE:
      return 'รับเงินแล้ว';
  }
};

const getPaymentReceivedStatusColor = (
  status: EPaymentReceivedStatus,
): IColor => {
  let color: IColor = {
    bg: 'bg-blue-200',
    text: 'text-black',
  };
  switch (status) {
    case EPaymentReceivedStatus.BILLED:
      color = {
        bg: 'bg-orange-100',
        text: 'text-orange-700',
      };
      break;
    case EPaymentReceivedStatus.RECEIVED:
      color = {
        bg: 'bg-purple-100',
        text: 'text-purple-700',
      };
      break;
    case EPaymentReceivedStatus.DONE:
      color = {
        bg: 'bg-green-100',
        text: 'text-green-700',
      };
      break;
  }
  return color;
};

const getPaymentReceivedStatusOptions = () =>
  Object.entries(EPaymentReceivedStatus)
    .filter(([key]) => isNaN(Number(key)))
    .map(([, value]) => ({
      label: getPaymentReceivedStatusName(value as EPaymentReceivedStatus),
      value: value as EPaymentReceivedStatus,
    }));

export {
  getOrderStatusName,
  getOrderStatusColor,
  getOrderStatusOptions,
  getOrderStepStatus,
  isSuperUserEditOrderInfo,
  isEditOrderInfo,
  getPodUrl,
  getPaymentTypeOptions,
  getPaymentTypeColor,
  getPaymentTypeName,
  getPaymentReceivedStatusOptions,
  getPaymentReceivedStatusName,
  getPaymentReceivedStatusColor,
  getOrderTypeName,
  getOrderTypeColor,
};
